import { useMemo } from "react";

type VersionMutationState = {
  originalArgs?: { versionId: string };
  isLoading: boolean;
  isSuccess: boolean;
  reset: () => void;
};

/**
 * A hook that scopes an RTK mutation to a specific version.
 *
 * The mutation must take `versionId` as an argument and must not use `fixedCacheKey`. If the mutation was last
 * called for a different version, it is returned as not loading and not successful.
 *
 * @example
 * const [executeCustomWorkflow, { isLoading }] = useVersionScopedMutation(useExecuteCustomWorkflowMutation(), versionId);
 */
export function useVersionScopedMutation<Trigger>(
  [trigger, state]: readonly [Trigger, VersionMutationState],
  versionId: string | undefined
): readonly [Trigger, Omit<VersionMutationState, "originalArgs">] {
  return useMemo(() => {
    // false if the mutation was last called for a different version (or not at all).
    const isCurrentVersion = state.originalArgs?.versionId === versionId;

    // `reset` either clears the state for (i) the current version, or (ii) an old version. In either case we're
    // fine: (i) works as expected, and for (ii) the guards below make that reset a no-op.
    return [trigger, { isLoading: isCurrentVersion && state.isLoading, isSuccess: isCurrentVersion && state.isSuccess, reset: state.reset }];
  }, [trigger, state, versionId]);
}
